import { useDataStore, useFilterStore } from "@/stores";
import clsx from "clsx";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useXarrow } from "react-xarrows";

interface VersionFilterProps {
    shouldConfirm?: boolean;
    small?: boolean;
}

export interface VersionFilterRef {
    confirmChange: () => void;
}

const VersionFilter = forwardRef<VersionFilterRef, VersionFilterProps>(({ shouldConfirm, small }, ref) => {
    const versions = useDataStore((state) => state.versions);
    const activeVersion = useFilterStore((state) => state.activeVersion);
    const setActiveVersion = useFilterStore((state) => state.setActiveVersion);
    const setActiveModalVersion = useFilterStore((state) => state.setActiveModalVersion);
    const [unconfirmedVersion, setUnconfirmedVersion] = useState<string | null>(activeVersion);

    const { t } = useTranslation(["filters"]);
    const updateXarrow = useXarrow();

    useImperativeHandle(ref, () => ({
        confirmChange: () => {
            setActiveVersion(unconfirmedVersion);
        },
    }));

    useEffect(() => {
        updateXarrow();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeVersion]);

    return versions
        .sort((a, b) => b.publicationDate.getTime() - a.publicationDate.getTime())
        .map((version) => {
            const id = `version-${shouldConfirm ? "confirm" : "direct"}-${version.id}`;
            const isActive = activeVersion === version.id;
            const isUnconfirmed = unconfirmedVersion === version.id;
            const isSelected = shouldConfirm ? isUnconfirmed : isActive;

            const handleClick = () => {
                if (shouldConfirm) {
                    setUnconfirmedVersion(version.id);
                    setActiveModalVersion(version.id);
                } else {
                    setActiveVersion(version.id);
                }
            };

            return (
                <div key={version.id} className="group select-none">
                    <div
                        className={clsx([
                            small ? "flex h-[56px] gap-3 px-5 py-2" : "flex h-[90px] gap-3 p-5",
                            "rounded-md text-base font-medium items-center cursor-pointer",
                            "bg-primary-100 text-primary-500",
                            "hover:bg-primary-600 hover:text-white",
                            "group-has-checked:bg-primary-500 group-has-checked:text-white",
                        ])}
                        onClick={handleClick}
                    >
                        <div className="grid size-6 grid-cols-1 shrink-0">
                            <input
                                id={id}
                                name={id}
                                value={version.id}
                                type="radio"
                                checked={isSelected}
                                className={clsx([
                                    " cursor-pointer col-start-1 row-start-1 appearance-none rounded-full border-2 min-w-6 min-h-6",
                                    "border-primary-300 bg-primary-100",
                                    "checked:outline-2 checked:-outline-offset-4 checked:outline-primary-500",
                                ])}
                                onChange={() => {}}
                            />
                            <div className="pointer-events-none opacity-0 group-has-checked:opacity-100 bg-white w-4 h-4 rounded-full col-start-1 row-start-1 self-center justify-self-center" />
                            <div className="pointer-events-none opacity-0 group-has-checked:opacity-100 bg-primary-500 w-3 h-3 rounded-full col-start-1 row-start-1 self-center justify-self-center" />
                        </div>
                        <div className="flex flex-1 flex-wrap gap-1 items-center">
                            {t("versions.label", { ns: "filters" })} {version.name}
                            {version.isDefault && (
                                <span className={clsx("text-xs")}>{t("versions.latest", { ns: "filters" })}</span>
                            )}
                        </div>
                    </div>
                </div>
            );
        });
});

export default VersionFilter;
